import { useEffect, useMemo } from 'react';
import BrandLogo from './BrandLogo';
import CourtLines from './CourtLines';
import { getBuildBranding, getSnapshotImageUrls } from '../utils/buildBrandingUtil';
import { preloadImages } from '../utils/imagePreloadUtil';
import { tonalCompanion } from '../utils/colorUtil';
import { useFirstVisitSplash } from '../hooks/useFirstVisitSplash';
import {
  FIRST_VISIT_SPLASH_EXIT_MS,
  FIRST_VISIT_SPLASH_GREETING,
  FIRST_VISIT_SPLASH_GREETING_FALLBACK,
  FIRST_VISIT_SPLASH_POWERED_BY,
  FIRST_VISIT_SPLASH_TONE_SHIFT,
  FIRST_VISIT_SPLASH_TONE_MIN_CONTRAST,
} from '../constants/bookingConst';
import '../css/FirstVisitSplash.css';

/**
 * A short branded welcome on every page load of the guest pages and the
 * admin dashboard (first visit, refresh or return visit): a court draws itself in
 * a tone of the club's header colour, the club's logo drops onto the net, and
 * "Welcome to <club>" rises in beneath it.
 *
 * It stays up for REACT_APP_WELCOME_SCREEN_SECONDS (.env.common, default 5s)
 * and never waits for anything (see useFirstVisitSplash). It is an OVERLAY, not a gate: the app
 * tree mounts underneath it from the first frame, so App Check, the anonymous
 * sign-in (AuthProvider), the config reads and the landing page's images all
 * load while the guest watches; whatever is still loading when it ends
 * continues in the first screen.
 *
 * Logo, name and colours come from the build (see getBuildBranding), never
 * from Firebase, so the club's look is there on the first frame. The colours
 * are applied as CSS variables on the splash itself rather than read from the
 * --jt-* theme variables, because ConfigContext only injects those in an
 * effect — after the first paint.
 *
 * Not a modal — it has no close button and nothing to dismiss; it removes
 * itself when its time is up.
 *
 * @returns {JSX.Element|null}
 */
function FirstVisitSplash() {
  const { visible, leaving } = useFirstVisitSplash();
  const branding = useMemo(() => getBuildBranding(), []);

  // Uses the welcome's time on screen to download every static photo in the build, so
  // the time-slot screen's collage (and the rest) paints from the HTTP cache
  // when the guest gets there instead of popping in.
  // `visible` only ever goes true -> false, so this runs once per page load.
  useEffect(() => {
    if (visible) preloadImages(getSnapshotImageUrls());
  }, [visible]);

  // The page underneath is live and scrollable; lock it so a swipe on the
  // welcome does not scroll the landing page out from under it.
  useEffect(() => {
    if (!visible) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [visible]);

  if (!visible) return null;

  const { colors, clubName, logoUrl } = branding;
  const style = {
    // Config -> Theme: Screen Background (first frame), Header Background
    // (the welcome's background, and — as a lighter or darker tone of it —
    // the court drawing and club name), Court Name / Logo Color
    // ("Welcome to"). See FirstVisitSplash.css.
    '--fvs-bg': colors.background,
    '--fvs-header': colors.headerBackground,
    '--fvs-header-tone': tonalCompanion(
      colors.headerBackground,
      FIRST_VISIT_SPLASH_TONE_SHIFT,
      FIRST_VISIT_SPLASH_TONE_MIN_CONTRAST,
    ),
    '--fvs-brand': colors.brand,
    // Shared with the stylesheet so the exit animation stays in step with
    // the JS timer in useFirstVisitSplash.
    '--fvs-exit': `${FIRST_VISIT_SPLASH_EXIT_MS}ms`,
  };

  return (
    <div
      className={`fvs-screen${leaving ? ' is-leaving' : ''}`}
      style={style}
      role="status"
      aria-live="polite"
    >
      <div className="fvs-wipe" aria-hidden="true" />
      <div className="fvs-content">
        <div className="fvs-stage">
          <CourtLines className="fvs-court" />
          <span className="fvs-ripple" aria-hidden="true" />
          <div className="fvs-logo-drop">
            <BrandLogo src={logoUrl} alt={clubName} className="fvs-logo" />
          </div>
        </div>
        {clubName ? (
          <p className="fvs-greeting">
            <span className="fvs-greeting-eyebrow">{FIRST_VISIT_SPLASH_GREETING}</span>
            <span className="fvs-greeting-name">{clubName}</span>
          </p>
        ) : (
          <p className="fvs-greeting">
            <span className="fvs-greeting-name">{FIRST_VISIT_SPLASH_GREETING_FALLBACK}</span>
          </p>
        )}
        <p className="fvs-powered-by">{FIRST_VISIT_SPLASH_POWERED_BY}</p>
      </div>
    </div>
  );
}

export default FirstVisitSplash;
